import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import {
  ProductionSceneResource,
  ProductionSceneVoiceoverResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";

type FormDataT = {
  element: string;
  assignee: string;
  date_time?: string;
  note?: string;
  production_scene_id?: number;
};

interface Props {
  onClose: () => void;
  scenes?: number[];
  voiceover?: ProductionSceneVoiceoverResource;
  scene?: ProductionSceneResource;
}

const VoiceoverModal: FC<Props> = ({ onClose, scenes, voiceover, scene }) => {
  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      element: voiceover?.element ?? "",
      assignee: voiceover?.assignee ?? "",
      date_time: voiceover?.time ?? "",
      note: voiceover?.note ?? "",
      production_scene_id: scene?.id,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (voiceover) {
          await api.put<never, { id: number }>(
            `/v1/production/scenes/voiceovers/${voiceover.id}`,
            data
          );
        } else {
          await api.post<never, { id: number }>(
            "/v1/production/scenes/voiceovers",
            data
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Voiceover has been successfully ${voiceover ? "updated" : "created"}
          `
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!voiceover ? "Add" : "Edit"} voiceover`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="element"
                type="textarea"
                label="Element"
              />
              {!!scenes?.length && (
                <FormField<FormDataT, number>
                  control={control}
                  name="production_scene_id"
                  label="Scene number"
                  type="select"
                  options={scenes.map((scene, index) => {
                    return {
                      label: (index + 1).toString(),
                      value: scene,
                    };
                  })}
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="assignee"
                label="Assignee"
                suggestionsEndpoint="/v1/production/scenes/voiceovers/assignees"
              />
              <FormField<FormDataT>
                control={control}
                name="date_time"
                label="Start Date & Time"
                id="date"
                type="datepicker"
                datepickerType="datetime"
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                label="Notes"
                type="textarea"
                id="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default VoiceoverModal;
